import { fetchCloudflareContentBlob } from 'api/apiCloudflare';

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => resolve('');
        reader.readAsDataURL(blob);
    });

const blobToPngDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(blob);
        const image = new Image();

        image.onload = () => {
            try {
                const width = Math.max(1, image.naturalWidth || image.width || 1);
                const height = Math.max(1, image.naturalHeight || image.height || 1);
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve('');
                    return;
                }
                ctx.drawImage(image, 0, 0, width, height);
                resolve(canvas.toDataURL('image/png'));
            } catch (_err) {
                resolve('');
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        };

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve('');
        };

        image.src = objectUrl;
    });

const fetchBlobDirect = async (fileUrl: string): Promise<Blob | null> => {
    try {
        const response = await fetch(fileUrl, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) return null;
        const blob = await response.blob();
        return blob.size ? blob : null;
    } catch (_e) {
        return null;
    }
};

const isPdfNativeImage = (blob: Blob) => {
    const type = String(blob.type || '').toLowerCase();
    return type.includes('image/jpeg') || type.includes('image/jpg') || type.includes('image/png');
};

/**
 * Load a remote image into a data URL for pdfmake.
 * Production origins are usually blocked by R2 CORS on direct fetch, so remote http(s)
 * URLs go through the authenticated API proxy first.
 */
export const fileToPdfCompatibleDataUrl = async (fileUrl: string): Promise<string> => {
    const src = String(fileUrl || '')
        .trim()
        .replace(/&amp;/g, '&');
    if (!src) return '';
    if (src.startsWith('data:image/')) return src;

    const isRemote = /^https?:\/\//i.test(src);
    // Prefer API proxy for remote CDN URLs (localhost often has looser CORS than production).
    const blob = isRemote
        ? (await fetchCloudflareContentBlob(src)) || (await fetchBlobDirect(src))
        : await fetchBlobDirect(src);

    if (!blob) {
        console.warn('[fileToPdfCompatibleDataUrl] failed to load image', src);
        return '';
    }

    // pdfmake supports jpeg/png natively; webp/svg/etc need canvas → png.
    if (isPdfNativeImage(blob)) {
        const dataUrl = await blobToDataUrl(blob);
        if (dataUrl.startsWith('data:image/')) return dataUrl;
    }

    const pngDataUrl = await blobToPngDataUrl(blob);
    if (!pngDataUrl) {
        console.warn('[fileToPdfCompatibleDataUrl] failed to convert image', src, blob.type);
    }
    return pngDataUrl;
};

export default fileToPdfCompatibleDataUrl;
